import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, merge } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { WebSocketService } from './websocket.service';
import { AuthService } from './auth.service';

export interface MarinaPriceList {
  id?: string;
  name: string;
  description?: string;
  currencyCode: string;
  validFrom?: string;
  validTo?: string;
  marinaId: string;
  marinaName?: string;
  clientId: string;
  isDefault?: boolean;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateUpdateMarinaPriceListDto {
  name: string;
  description?: string;
  currencyCode: string;
  validFrom?: string;
  validTo?: string;
  marinaId: string;
  clientId: string;
  isDefault?: boolean;
  isActive?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class MarinaPriceListService {
  private apiUrl = '/api/v1/price-lists';

  constructor(
    private http: HttpClient,
    private router: Router,
    private webSocketService: WebSocketService,
    private authService: AuthService
  ) {
    console.log('[MarinaPriceListService] Service initialized with WebSocket support');
  }

  /**
   * Get all price lists
   */
  getPriceLists(): Observable<MarinaPriceList[]> {
    const headers = this.getAuthHeaders();

    return this.http.get<MarinaPriceList[]>(this.apiUrl, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching price lists:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get price list by ID
   */
  getPriceListById(id: string): Observable<MarinaPriceList> {
    const headers = this.getAuthHeaders();

    return this.http.get<MarinaPriceList>(`${this.apiUrl}/${id}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching price list by ID:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get price lists by marina ID
   */
  getPriceListsByMarinaId(marinaId: string): Observable<MarinaPriceList[]> {
    const headers = this.getAuthHeaders();

    return this.http.get<MarinaPriceList[]>(`${this.apiUrl}/marina/${marinaId}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching price lists by marina ID:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Create a new price list
   */
  createPriceList(priceList: CreateUpdateMarinaPriceListDto): Observable<MarinaPriceList> {
    const headers = this.getAuthHeaders();

    return this.http.post<MarinaPriceList>(this.apiUrl, priceList, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error creating price list:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update an existing price list
   */
  updatePriceList(id: string, priceList: Partial<CreateUpdateMarinaPriceListDto>): Observable<MarinaPriceList> {
    const headers = this.getAuthHeaders();

    return this.http.put<MarinaPriceList>(`${this.apiUrl}/${id}`, priceList, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error updating price list:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete a price list
   */
  deletePriceList(id: string): Observable<void> {
    const headers = this.getAuthHeaders();

    return this.http.delete<void>(`${this.apiUrl}/${id}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error deleting price list:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get authentication headers from localStorage
   */
  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('auth_token');
    const credentials = localStorage.getItem('auth_credentials');

    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    } else if (credentials) {
      headers = headers.set('Authorization', `Basic ${credentials}`);
    }

    return headers;
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(error: HttpErrorResponse): void {
    if (error.status === 401) {
      // Clear stored credentials and redirect to login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_credentials');
      localStorage.removeItem('current_user');
      this.router.navigate(['/login']);
    }
  }

  /**
   * WebSocket Integration Methods
   */

  /**
   * Get observable for real-time price list updates from WebSocket (create/update)
   */
  getRealtimePriceListUpdates(): Observable<any> {
    const clientIds = this.authService.getClientIds();
    const observables = clientIds.map(clientId => 
      this.webSocketService.subscribe(`/topic/${clientId}.MarinaPriceList`)
    );
    return merge(...observables);
  }

  /**
   * Get observable for real-time price list deletions from WebSocket
   */
  getRealtimePriceListDeletions(): Observable<any> {
    const clientIds = this.authService.getClientIds();
    const observables = clientIds.map(clientId => 
      this.webSocketService.subscribe(`/topic/${clientId}.MarinaPriceList`)
    );
    return merge(...observables);
  }
}

